/**
 * Utility functions for Registration Details
 */

const parseDateValue = (value: any): Date | null => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;

  const str = String(value).trim();
  const datePart = str.split('T')[0];
  const match = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    // Use local-noon to avoid timezone day-shift issues.
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12, 0, 0, 0);
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Calculate number of nights between arrival and departure dates
 */
export const calculateNights = (arrivalDate: any, departureDate: any): number => {
  if (!arrivalDate || !departureDate) return 0;
  
  const arrival = parseDateValue(arrivalDate);
  const departure = parseDateValue(departureDate);
  if (!arrival || !departure) return 0;
  const timeDiff = departure.getTime() - arrival.getTime();
  const nights = Math.ceil(timeDiff / (1000 * 3600 * 24));
  
  return nights > 0 ? nights : 0;
};

/**
 * Format date with day of week
 */
export const formatDateWithDay = (dateString: string): string => {
  if (!dateString) return 'Not set';
  const date = parseDateValue(dateString);
  if (!date) return 'Not set';

  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Check if stay duration qualifies as full camp (5+ nights)
 */
export const isFullCamp = (nights: number): boolean => {
  return nights >= 5;
};

/**
 * Get display text for nights (e.g., "Full Camp" or "3 nights")
 */
export const getNightsDisplayText = (nights: number): string => {
  return isFullCamp(nights) ? 'Full Camp' : `${nights} nights`;
};

/**
 * Convert date to YYYY-MM-DD format for input fields
 */
export const toInputDateFormat = (dateString: string): string => {
  if (!dateString) return '';
  const date = parseDateValue(dateString);
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

